import type { ComponentDefinition, Terminal } from "../types";

type Pole = "l" | "n" | "pe";
type Link = readonly [string, string];
const POLES: readonly Pole[] = ["l", "n", "pe"];

function terminal(id: string, label: string, ratingA: number, direction: Terminal["direction"], side: Terminal["side"], offset: number): Terminal {
  return { id, label, maxAmp: ratingA, polarity: id.endsWith("-pe") ? "ground" : "neutral", direction, side, offset };
}

function port(prefix: string, label: string, ratingA: number, direction: Terminal["direction"], side: Terminal["side"], start = 0.2, step = 0.3): Terminal[] {
  return POLES.map((pole, i) => terminal(`${prefix}${pole}`, `${label} ${pole.toUpperCase()}`, ratingA, direction, side, start + i * step));
}

function links(from: string, to: string, poles: readonly Pole[] = POLES): Link[] {
  return poles.map(pole => [`${from}${pole}`, `${to}${pole}`] as const);
}

function outlet(voltage: 120 | 230, ratingA: number): ComponentDefinition {
  return {
    id: `dist-ac-outlet-${voltage}`,
    name: `AC Socket ${voltage}V ${ratingA}A`,
    shortName: `${voltage}V socket`,
    subtitle: "Generic earthed socket planning profile",
    category: "distribution",
    acVoltage: voltage,
    width: 210, height: 140,
    terminals: [
      ...port("ac-in-", "WIRING", ratingA, "input", "left"),
      ...port("ac-out-", "PLUG", ratingA, "output", "right"),
    ],
    simulateStates: { default: "connected", options: [{ id: "connected", label: "Connected", intraLinks: links("ac-in-", "ac-out-") }] },
  };
}

function load(voltage: 120 | 230): ComponentDefinition {
  return {
    id: `load-ac-${voltage}`,
    name: `AC Appliance ${voltage}V 1000W`,
    shortName: `${voltage}V appliance`,
    subtitle: "Generic AC load · edit input watts",
    category: "load",
    acVoltage: voltage,
    // A user-editable example input power, not measured consumption. Do not
    // add ampDraw or loadCurrentModel: these belong to the DC demand engine.
    watts: 1000,
    width: 210, height: 140,
    // No product was selected, so connector ampacity is unknown.
    terminals: port("ac-", "SUPPLY", 0, "input", "left"),
  };
}

function microwave(voltage: 120 | 230): ComponentDefinition {
  return {
    ...load(voltage),
    id: `load-microwave-ac-${voltage}`,
    name: `AC Microwave ${voltage}V`,
    shortName: `Microwave ${voltage}V`,
    subtitle: "Generic AC microwave · edit input watts",
    dutyHoursPerDay: 0.1,
  };
}

function panel(voltage: 120 | 230, mainA: number, branchA: number): ComponentDefinition {
  const branchIds = [1, 2, 3];
  const earth = branchIds.flatMap(i => links("ac-in-", `ac-branch${i}-`, ["pe"]));
  const neutral = branchIds.flatMap(i => links("ac-in-", `ac-branch${i}-`, ["n"]));
  const live = (enabled: readonly number[]) => enabled.flatMap(i => links("ac-in-", `ac-branch${i}-`, ["l"]));
  return {
    id: `dist-ac-panel-${voltage}`,
    name: `AC Distribution Panel ${voltage}V ${mainA}A · 3 Branches`,
    shortName: `${voltage}V panel`,
    subtitle: `Planning profile · ${mainA}A main, 3 × ${branchA}A branches`,
    category: "distribution",
    acVoltage: voltage,
    width: 270, height: 340,
    terminals: [
      ...port("ac-in-", "MAIN", mainA, "input", "left"),
      ...branchIds.flatMap((i, index) => port(`ac-branch${i}-`, `BRANCH ${i}`, branchA, "output", "right", 0.08 + index * 0.32, 0.10)),
    ],
    // This declared planning configuration has a two-pole main and three
    // single-pole branch breakers. Neutral and PE buses are never joined.
    // All eight branch combinations remain available without claiming an
    // automatic trip model; opening the main still leaves PE continuous.
    simulateStates: {
      default: "all-on",
      options: [
        { id: "all-on", label: "All branches on", intraLinks: [...earth, ...neutral, ...live(branchIds)] },
        { id: "branch-1", label: "Branch 1 only", intraLinks: [...earth, ...neutral, ...live([1])] },
        { id: "branch-2", label: "Branch 2 only", intraLinks: [...earth, ...neutral, ...live([2])] },
        { id: "branch-3", label: "Branch 3 only", intraLinks: [...earth, ...neutral, ...live([3])] },
        { id: "branches-1-2", label: "Branches 1 + 2", intraLinks: [...earth, ...neutral, ...live([1, 2])] },
        { id: "branches-1-3", label: "Branches 1 + 3", intraLinks: [...earth, ...neutral, ...live([1, 3])] },
        { id: "branches-2-3", label: "Branches 2 + 3", intraLinks: [...earth, ...neutral, ...live([2, 3])] },
        { id: "branches-off", label: "All branches off", intraLinks: [...earth, ...neutral] },
        { id: "main-off", label: "Main off", intraLinks: earth },
      ],
    },
  };
}

function selector(voltage: 120 | 230, ratingA: number): ComponentDefinition {
  const sources = ["shore", "inverter", "generator"] as const;
  const earth = sources.flatMap(source => links(`ac-${source}-`, "ac-out-", ["pe"]));
  return {
    id: `dist-ac-selector-${voltage}`,
    name: `Manual AC Source Selector ${voltage}V ${ratingA}A`,
    shortName: `${voltage}V source selector`,
    subtitle: "Shore / inverter / generator · one source at a time",
    category: "distribution",
    acVoltage: voltage,
    width: 270, height: 340,
    terminals: [
      ...sources.flatMap((source, index) => port(`ac-${source}-`, source.toUpperCase(), ratingA, "input", "left", 0.08 + index * 0.32, 0.10)),
      ...port("ac-out-", "OUTPUT", ratingA, "output", "right"),
    ],
    simulateStates: {
      default: "shore",
      options: [
        ...sources.map(source => ({
          id: source,
          label: source[0].toUpperCase() + source.slice(1),
          intraLinks: [...earth, ...links(`ac-${source}-`, "ac-out-", ["l", "n"])],
        })),
        { id: "off", label: "Off", intraLinks: earth },
      ],
    },
  };
}

export const AC_CIRCUITS: ComponentDefinition[] = [
  outlet(120, 15),
  outlet(230, 16),
  {
    // Leviton GFTR2-MW product page: 20 A, 125 V, Class A GFCI,
    // 5 mA ±1 mA. LINE/LOAD and receptacle-face connections are distinct.
    // https://leviton.com/products/gftr2-mw
    id: "dist-ac-gfci-leviton-gftr2",
    name: "Leviton GFTR2-MW GFCI Receptacle 20A 125V",
    shortName: "GFCI 20A",
    subtitle: "Class A personnel protection · 120V circuit",
    category: "distribution",
    manufacturer: "Leviton",
    model: "GFTR2-MW",
    // Nominal circuit voltage; 125 V above is the manufacturer's device rating.
    acVoltage: 120,
    width: 260, height: 240,
    terminals: [
      terminal("ac-in-l", "LINE L", 20, "input", "left", 0.25),
      terminal("ac-in-n", "LINE N", 20, "input", "left", 0.55),
      terminal("ac-pe", "GROUND / PLUG PE", 20, "bidi", "bottom", 0.5),
      terminal("ac-out-l", "LOAD L", 20, "output", "right", 0.15),
      terminal("ac-out-n", "LOAD N", 20, "output", "right", 0.35),
      terminal("ac-socket-l", "PLUG L", 20, "output", "right", 0.65),
      terminal("ac-socket-n", "PLUG N", 20, "output", "right", 0.85),
    ],
    // PE uses one common physical terminal and is unaffected by the contacts.
    // No ampOverride sentinel: it would incorrectly suppress all earth paths.
    simulateStates: {
      default: "reset",
      options: [
        { id: "reset", label: "Reset / connected", intraLinks: [
          ...links("ac-in-", "ac-out-", ["l", "n"]),
          ...links("ac-in-", "ac-socket-", ["l", "n"]),
        ] },
        { id: "tripped", label: "Tripped / test", intraLinks: [] },
      ],
    },
  },
  load(120),
  load(230),
  microwave(120),
  microwave(230),
  panel(120, 30, 15),
  panel(230, 16, 10),
  selector(120, 30),
  selector(230, 16),
];

export type AcCircuitProfile = {
  kind: "outlet" | "load" | "gfci" | "panel" | "selector";
  nominalVoltage: 120 | 230;
  ratingA?: number;
  mainRatingA?: number;
  leakageTripMa?: number;
  requiredPe: readonly string[];
  /** True means the ratings/configuration are declared example choices. */
  planning: boolean;
};

/** Reviewed topology semantics, separate from persisted custom definitions.
 * Consumers must validate the definition AND effective instance terminals. */
export const AC_CIRCUIT_PROFILES: Readonly<Record<string, AcCircuitProfile>> = {
  "dist-ac-outlet-120": { kind: "outlet", nominalVoltage: 120, ratingA: 15, requiredPe: ["ac-in-pe", "ac-out-pe"], planning: true },
  "dist-ac-outlet-230": { kind: "outlet", nominalVoltage: 230, ratingA: 16, requiredPe: ["ac-in-pe", "ac-out-pe"], planning: true },
  "dist-ac-gfci-leviton-gftr2": { kind: "gfci", nominalVoltage: 120, ratingA: 20, leakageTripMa: 5, requiredPe: ["ac-pe"], planning: false },
  "load-ac-120": { kind: "load", nominalVoltage: 120, requiredPe: ["ac-pe"], planning: true },
  "load-ac-230": { kind: "load", nominalVoltage: 230, requiredPe: ["ac-pe"], planning: true },
  "load-microwave-ac-120": { kind: "load", nominalVoltage: 120, requiredPe: ["ac-pe"], planning: true },
  "load-microwave-ac-230": { kind: "load", nominalVoltage: 230, requiredPe: ["ac-pe"], planning: true },
  "dist-ac-panel-120": { kind: "panel", nominalVoltage: 120, ratingA: 30, mainRatingA: 30, requiredPe: ["ac-in-pe", "ac-branch1-pe", "ac-branch2-pe", "ac-branch3-pe"], planning: true },
  "dist-ac-panel-230": { kind: "panel", nominalVoltage: 230, ratingA: 16, mainRatingA: 16, requiredPe: ["ac-in-pe", "ac-branch1-pe", "ac-branch2-pe", "ac-branch3-pe"], planning: true },
  "dist-ac-selector-120": { kind: "selector", nominalVoltage: 120, ratingA: 30, requiredPe: ["ac-shore-pe", "ac-inverter-pe", "ac-generator-pe", "ac-out-pe"], planning: true },
  "dist-ac-selector-230": { kind: "selector", nominalVoltage: 230, ratingA: 16, requiredPe: ["ac-shore-pe", "ac-inverter-pe", "ac-generator-pe", "ac-out-pe"], planning: true },
};

export type AcProtectionProfile = { from: string; to: string; ratingA: number; leakage?: boolean };

/** Declared branch overcurrent paths. GFCI is deliberately absent: personnel
 * leakage protection cannot establish an overload/short-circuit rating. */
export const AC_PROTECTION_PROFILES: Readonly<Record<string, readonly AcProtectionProfile[]>> = {
  "dist-ac-panel-120": [1, 2, 3].map(i => ({ from: "ac-in-l", to: `ac-branch${i}-l`, ratingA: 15 })),
  "dist-ac-panel-230": [1, 2, 3].map(i => ({ from: "ac-in-l", to: `ac-branch${i}-l`, ratingA: 10 })),
};

function electricalShape(definition: ComponentDefinition): string {
  const terminalShape = definition.terminals.map(({ id, polarity, direction, maxAmp }) =>
    ({ id, polarity, direction, maxAmp })).sort((a, b) => a.id.localeCompare(b.id));
  const states = definition.simulateStates && {
    default: definition.simulateStates.default,
    options: definition.simulateStates.options.map(({ id, ampMultiplier, ampOverride, role, intraLinks }) => ({
      id, ampMultiplier, ampOverride, role,
      intraLinks: intraLinks?.map(pair => [...pair].sort()).sort((a, b) => a.join(":").localeCompare(b.join(":"))),
    })).sort((a, b) => a.id.localeCompare(b.id)),
  };
  return JSON.stringify({
    category: definition.category,
    renderVariant: definition.renderVariant,
    manufacturer: definition.manufacturer, model: definition.model,
    acVoltage: definition.acVoltage, voltage: definition.voltage,
    inputVoltage: definition.inputVoltage, outputVoltage: definition.outputVoltage,
    ampDraw: definition.ampDraw, ampOutput: definition.ampOutput,
    ampSurge: definition.ampSurge, loadCurrentModel: definition.loadCurrentModel,
    isFuseBlock: definition.isFuseBlock, fuseSlotType: definition.fuseSlotType,
    internalDcConnections: definition.internalDcConnections, oneWayDcConnections: definition.oneWayDcConnections,
    terminals: terminalShape, states,
  });
}

/** Names, positions and a generic appliance's input watts may be customized;
 * copied IDs with different ratings/poles do not inherit safety semantics.
 * This validates a definition only; node-level overrides need a separate check. */
export function matchesAcCircuitProfileDefinition(definition: ComponentDefinition): boolean {
  const reference = AC_CIRCUITS.find(candidate => candidate.id === definition.id);
  return reference != null && electricalShape(definition) === electricalShape(reference);
}
